/**
 * L10 — EL PROYECTO EFECTIVO DE UNA LÍNEA DE COMPRA. CARRIL WEB.
 *
 * Gemelo de `mcp-server/src/services/purchaseProjectTag.js`. Existe por
 * duplicado porque el bundle del browser no puede importar del paquete
 * `mcp-server` — mismo motivo que `materialSplit`, `auditRef` y `stockExit`.
 * Si cambia una regla acá, cambia allá.
 *
 * `purchase_items.project_id` es la columna por la que filtra TODO consumidor
 * de costo (el comprometido del presupuesto, el gasto que se asienta al
 * facturar, el resumen del proyecto). El COMMENT de la columna en la base
 * declara que se hereda sola desde `purchases.project_id` "at insert time" y
 * ESO ES FALSO: el único trigger sobre `purchase_items` es
 * `trg_purchase_items_recalc` (totales), la columna no tiene DEFAULT y ningún
 * UPDATE posterior la repara. La herencia la tiene que escribir la aplicación.
 *
 * @module utils/purchaseProjectTag
 */

/**
 * Estados en los que una línea sigue siendo una OBLIGACIÓN futura (aprobada,
 * todavía no facturada). Una vez 'invoiced' el costo pasa a `expenses` y
 * contarlo acá lo duplicaría.
 */
export const OPEN_PO_STATUSES = ['approved', 'sent', 'partial'];

const SELECT_COMMITTED =
  'quantity_ordered, quantity_received, unit_cost, project_id, purchases!inner(id, status, project_id)';

/**
 * El proyecto al que hay que cargarle esta línea: el suyo si lo tiene, el del
 * encabezado si no.
 *
 * @param {{project_id?: string|null}|null} line
 * @param {{project_id?: string|null}|null} header - fila de `purchases`
 * @returns {string|null}
 */
export const effectiveLineProjectId = (line, header) =>
  (line && line.project_id) || (header && header.project_id) || null;

/**
 * Líneas de órdenes ABIERTAS que le pertenecen a alguno de estos proyectos,
 * contando tanto las tagueadas como las que heredan del encabezado.
 *
 * DOS consultas a propósito: PostgREST no expresa un OR entre una columna de la
 * fila y una del recurso embebido (`purchases.project_id`). Dos consultas
 * disjuntas (`project_id` en la lista / `IS NULL` + encabezado en la lista) son
 * verificables una por una; un `or=(...)` mezclando niveles filtra de menos en
 * silencio, que es el modo de error caro.
 *
 * @param {object} client - cliente Supabase
 * @param {string[]} projectIds
 * @returns {Promise<Array<object & {effective_project_id: string|null, project_id_inherited: boolean}>>}
 */
export const fetchOpenCommittedLines = async (client, projectIds) => {
  const ids = [...new Set((projectIds || []).filter(Boolean))];
  if (ids.length === 0) return [];

  const tagged = await client
    .from('purchase_items')
    .select(SELECT_COMMITTED)
    .in('project_id', ids)
    .in('purchases.status', OPEN_PO_STATUSES);
  if (tagged?.error) throw tagged.error;

  const inherited = await client
    .from('purchase_items')
    .select(SELECT_COMMITTED)
    .is('project_id', null)
    .in('purchases.project_id', ids)
    .in('purchases.status', OPEN_PO_STATUSES);
  if (inherited?.error) throw inherited.error;

  return [...(tagged?.data || []), ...(inherited?.data || [])].map((line) => ({
    ...line,
    effective_project_id: effectiveLineProjectId(line, line?.purchases),
    project_id_inherited: !line?.project_id,
  }));
};

/**
 * Suma el comprometido (lo pedido y todavía no recibido) por proyecto efectivo.
 *
 * @param {Array<object>} lines - salida de `fetchOpenCommittedLines`
 * @returns {Map<string, number>}
 */
export const committedAmountByProject = (lines) => {
  const byProject = new Map();
  (lines || []).forEach((line) => {
    const projectId = line?.effective_project_id;
    if (!projectId) return;
    const remaining = Number(line.quantity_ordered || 0) - Number(line.quantity_received || 0);
    if (remaining <= 0) return;
    byProject.set(projectId, (byProject.get(projectId) || 0) + remaining * Number(line.unit_cost || 0));
  });
  return byProject;
};
